// Payroll runs — one row per calendar month. Generates payslips per employee
// using SA tax tables (PAYE), UIF (1% capped at R177.12 each side), SDL (1%).
import { useEffect, useMemo, useState } from 'react';
import { Plus, Calculator, CheckCircle, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { formatMoney } from '../lib/format';

const UIF_CEILING_GROSS = 17712;  // R17,712/month ceiling for UIF calc

function paye(annualGross: number, age = 30): number {
  let tax = 0;
  if (annualGross <= 237100)        tax = annualGross * 0.18;
  else if (annualGross <= 370500)   tax = 42678  + (annualGross - 237100) * 0.26;
  else if (annualGross <= 512800)   tax = 77362  + (annualGross - 370500) * 0.31;
  else if (annualGross <= 673000)   tax = 121475 + (annualGross - 512800) * 0.36;
  else if (annualGross <= 857900)   tax = 179147 + (annualGross - 673000) * 0.39;
  else if (annualGross <= 1817000)  tax = 251258 + (annualGross - 857900) * 0.41;
  else                              tax = 644489 + (annualGross - 1817000) * 0.45;
  let rebate = 17235;
  if (age >= 65) rebate += 9444;
  if (age >= 75) rebate += 3145;
  return Math.max((tax - rebate) / 12, 0);
}

function ageFromId(id: string | null): number {
  if (!id || id.length < 6) return 30;
  const yy = parseInt(id.slice(0, 2));
  const mm = parseInt(id.slice(2, 4));
  const dd = parseInt(id.slice(4, 6));
  const fullYear = yy < 30 ? 2000 + yy : 1900 + yy;
  const dob = new Date(fullYear, mm - 1, dd);
  return Math.floor((Date.now() - dob.getTime()) / (365.25 * 24 * 3600 * 1000));
}

export default function Payroll() {
  const { currentOrg } = useOrg();
  const [runs, setRuns] = useState<any[]>([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  async function load() {
    if (!currentOrg) return;
    const { data } = await supabase.from('dodobooks_payroll_runs').select('*')
      .eq('org_id', currentOrg.id).order('period_year', { ascending: false }).order('period_month', { ascending: false });
    setRuns(data ?? []);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [currentOrg]);

  async function createRun() {
    if (!currentOrg) return;
    setBusy(true); setMsg(null);
    // Fetch active employees + check for existing run.
    const { data: existing } = await supabase.from('dodobooks_payroll_runs').select('id')
      .eq('org_id', currentOrg.id).eq('period_year', year).eq('period_month', month).maybeSingle();
    if (existing) { setMsg('A run for this month already exists.'); setBusy(false); return; }
    const { data: emps } = await supabase.from('dodobooks_employees').select('*')
      .eq('org_id', currentOrg.id).eq('is_archived', false);
    const employees = emps ?? [];
    const payDate = new Date(year, month - 1, 25).toISOString().slice(0, 10);

    // Sum totals while we go.
    let totals = { gross: 0, paye: 0, uif_e: 0, uif_er: 0, sdl: 0, net: 0 };
    const slipsToInsert: any[] = [];
    for (const e of employees) {
      const gross = Number(e.base_salary) || 0;
      const age = ageFromId(e.id_number);
      const monthlyPaye = paye(gross * 12, age);
      const uifBase = Math.min(gross, UIF_CEILING_GROSS);
      const uifEmp = e.uif_applicable ? uifBase * 0.01 : 0;
      const uifEmployer = e.uif_applicable ? uifBase * 0.01 : 0;
      const sdl = e.sdl_applicable ? gross * 0.01 : 0;
      const net = gross - monthlyPaye - uifEmp;
      totals.gross += gross; totals.paye += monthlyPaye; totals.uif_e += uifEmp;
      totals.uif_er += uifEmployer; totals.sdl += sdl; totals.net += net;
      slipsToInsert.push({
        org_id: currentOrg.id, employee_id: e.id,
        gross, paye: monthlyPaye, uif_employee: uifEmp, uif_employer: uifEmployer,
        sdl, other_deductions: 0, net_pay: net,
      });
    }

    const { data: run, error } = await supabase.from('dodobooks_payroll_runs').insert({
      org_id: currentOrg.id, period_year: year, period_month: month, pay_date: payDate, status: 'draft',
      total_gross: totals.gross, total_paye: totals.paye,
      total_uif_employee: totals.uif_e, total_uif_employer: totals.uif_er,
      total_sdl: totals.sdl, total_net: totals.net,
    }).select('id').single();
    if (error) { setMsg(error.message); setBusy(false); return; }
    if (slipsToInsert.length) {
      const rows = slipsToInsert.map((s) => ({ ...s, payroll_run_id: run.id }));
      await supabase.from('dodobooks_payslips').insert(rows);
    }
    setBusy(false);
    setMsg(`Run created with ${slipsToInsert.length} payslip${slipsToInsert.length === 1 ? '' : 's'}.`);
    load();
  }

  const monthName = useMemo(() => new Date(year, month - 1, 1).toLocaleString('en-ZA', { month: 'long' }), [year, month]);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="h-page flex items-center gap-2"><Calculator size={22} /> Payroll runs</h1>
        <p className="text-sm text-text-mute mt-1">One run per calendar month. Generates payslips with SA PAYE, UIF (1% capped at R177.12) and SDL (1%). Drives EMP201 and EMP501.</p>
      </div>

      <div className="card">
        <h2 className="h-section text-base mb-3">New run</h2>
        <div className="grid grid-cols-3 gap-3 mb-3">
          <div className="field"><label>Year</label><input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} /></div>
          <div className="field"><label>Month</label>
            <select value={month} onChange={(e) => setMonth(Number(e.target.value))}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>{new Date(2000, m - 1, 1).toLocaleString('en-ZA', { month: 'long' })}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button className="btn btn-primary w-full" onClick={createRun} disabled={busy}><Plus size={14} />{busy ? ' Calculating…' : ` Create run for ${monthName}`}</button>
          </div>
        </div>
        {msg && <div className="text-sm text-emerald bg-emerald/10 border border-emerald/30 px-3 py-2 rounded-md">{msg}</div>}
      </div>

      <div className="card overflow-x-auto">
        <h2 className="h-section text-base mb-3">Past runs</h2>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-text-mute uppercase tracking-widest font-mono">
              <th className="px-3 py-2">Period</th>
              <th className="px-3 py-2 text-right">Gross</th>
              <th className="px-3 py-2 text-right">PAYE</th>
              <th className="px-3 py-2 text-right">UIF (er + emp)</th>
              <th className="px-3 py-2 text-right">SDL</th>
              <th className="px-3 py-2 text-right">Net</th>
              <th className="px-3 py-2">EMP201</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.id} className="border-t border-line">
                <td className="px-3 py-2 font-semibold">{new Date(r.period_year, r.period_month - 1, 1).toLocaleString('en-ZA', { month: 'long', year: 'numeric' })}</td>
                <td className="px-3 py-2 text-right font-mono">{formatMoney(r.total_gross, 'ZAR')}</td>
                <td className="px-3 py-2 text-right font-mono">{formatMoney(r.total_paye, 'ZAR')}</td>
                <td className="px-3 py-2 text-right font-mono">{formatMoney((Number(r.total_uif_employee) + Number(r.total_uif_employer)), 'ZAR')}</td>
                <td className="px-3 py-2 text-right font-mono">{formatMoney(r.total_sdl, 'ZAR')}</td>
                <td className="px-3 py-2 text-right font-mono font-semibold">{formatMoney(r.total_net, 'ZAR')}</td>
                <td className="px-3 py-2 text-xs">{r.emp201_filed_at ? <span className="text-emerald inline-flex items-center gap-1"><CheckCircle size={12} /> filed</span> : <span className="text-text-mute">draft</span>}</td>
                <td className="px-3 py-2 text-right"><Link to={`/emp201?year=${r.period_year}&month=${r.period_month}`} className="text-emerald hover:underline inline-flex items-center gap-1 text-xs">EMP201 <ChevronRight size={12} /></Link></td>
              </tr>
            ))}
            {runs.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-text-mute text-sm">No payroll runs yet. Create one above.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
